import { logger } from '../../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { AppPageHeader } from '../../components/app/AppPage';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  CircularProgress,
  Alert,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Link,
  Chip,
  Grid,
  FormControlLabel,
  Radio
} from '@mui/material';
import taxService from '../../services/taxService';
import { companyService } from '../../services/companyService';
import { useSnackbar } from 'notistack';
import { Link as RouterLink } from 'react-router-dom';
import { mapErrorToUserMessage } from '../../utils/errorMapping';

const US_STATES = [
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' }
];

// Registration types supported
const US_REGISTRATION_TYPES = [
  { value: 'state_sales_tax', label: 'State Sales Tax' },
  { value: 'local_lease_tax', label: 'Local Lease Tax' },
  { value: 'state_communications_tax', label: 'State Communications Tax' },
  { value: 'local_amusement_tax', label: 'Local Amusement Tax' },
  { value: 'state_retail_delivery_fee', label: 'State Retail Delivery Fee' }
];

const TaxRegistration = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [country, setCountry] = useState('US'); // Default to US
  const [state, setState] = useState('CA');
  const [registrationType, setRegistrationType] = useState('state_sales_tax');
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [useImmediateStart, setUseImmediateStart] = useState(true); // Default to immediate start
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Update: Add selectedCompanyData state to store full company details
  const [selectedCompanyData, setSelectedCompanyData] = useState(null);
  
  // Connected account support
  const [selectedConnectedAccount, setSelectedConnectedAccount] = useState('');

  // Calculate tomorrow's date for min date validation
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };
  
  // Set startDate to tomorrow by default
  useEffect(() => {
    setStartDate(getTomorrowDate());
  }, []);

  // Fetch existing registrations
  const fetchRegistrations = useCallback(async () => {
    setListLoading(true);
    try {
      // Build query params based on company selection
      const params = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      
      const data = await taxService.getTaxRegistrations(params);
      
      // Ensure we always set an array
      if (Array.isArray(data)) {
        setRegistrations(data);
      } else if (data && typeof data === 'object' && Array.isArray(data.registrations)) {
        // Handle case where API returns {registrations: [...]}
        setRegistrations(data.registrations);
      } else if (data && typeof data === 'object' && Array.isArray(data.data)) {
        // Handle case where API returns {data: [...]}
        setRegistrations(data.data);
      } else {
        // Handle case where data is undefined or not an array
        setRegistrations([]);
        logger.warn('No registration data returned from API or data is not an array:', data);
      }
    } catch (error) {
      logger.error('Failed to load registrations', error);
      
      // Check if it's a 404 error, which just means no registrations exist yet
      if (error.response && error.response.status === 404) {
        // For 404 errors, we just set empty registrations without showing an error
        setRegistrations([]);
        logger.log('No registrations found (404 response)');
      } else {
        // For other errors, still set the error state but don't show it directly
        const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
        logger.error(`Registration fetch error: ${errorMessage}`);
        
        // Only show the error to the user if it's not a 404
        if (!(error.response && error.response.status === 404)) {
          setError('Failed to load registrations: ' + errorMessage);
        }
        
        // Set registrations to empty array in case of error
        setRegistrations([]);
      }
    } finally {
      setListLoading(false);
    }
  }, [selectedCompany]); // Depend on selectedCompany to refetch when changed

  // Load companies for the dropdown
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoadingCompanies(true);
        const response = await companyService.getCompanies();
        setCompanies(response || []); // Ensure we have an array even if response is undefined
        setLoadingCompanies(false);
      } catch (error) {
        logger.error('Failed to load companies', error);
        if (enqueueSnackbar) {
          enqueueSnackbar('Failed to load companies', { variant: 'error' });
        }
        setCompanies([]);
        setLoadingCompanies(false);
      }
    };

    fetchCompanies();
  }, [enqueueSnackbar]);
  
  // Handle company selection
  const handleCompanyChange = async (event) => {
    const companyId = event.target.value;
    setSelectedCompany(companyId);
    
    // Clear existing registrations data when company changes
    setSelectedCompanyData(null);
    
    // If company is selected, fetch its details and auto-populate state
    if (companyId) {
      try {
        // Fetch company details
        const companyData = await companyService.getCompany(companyId);
        setSelectedCompanyData(companyData);
        
        // Auto-populate state from company's address (if it's a US company)
        if (companyData && (companyData.country === 'US' || !companyData.country)) {
          setState(companyData.state || 'CA');
        }
        
        // If company has a tax account, use it directly
        if (companyData && companyData.tax_account_id) {
          setSelectedConnectedAccount(companyData.tax_account_id);
        } else {
          setSelectedConnectedAccount('');
        }
      } catch (error) {
        logger.error('Failed to fetch company details', error);
        if (enqueueSnackbar) {
          enqueueSnackbar('Failed to fetch company details', { variant: 'error' });
        }
      }
    } else {
      setSelectedCompanyData(null);
      setSelectedConnectedAccount('');
    }
  };

  // Handle form submission
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!country) {
      setError('Please select a country');
      return;
    }
    
    if (country === 'US' && !state) {
      setError('Please select a state for US registrations');
      return;
    }
    
    if (!selectedCompany) {
      setError('Please select a company');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Prepare request body - simplified without existing registration logic
      const registrationData = {
        country,
        state: country === 'US' ? state : undefined,
        registration_type: registrationType,
        start_date: useImmediateStart ? 'now' : startDate,
        company_id: selectedCompany ? parseInt(selectedCompany, 10) : undefined,
        tax_account_id: selectedConnectedAccount || undefined
      };
      
      // Submit registration
      const result = await taxService.createTaxRegistration(registrationData);
      
      // Show success message
      if (result && result.id) {
        setSuccess(`Registration created successfully! ID: ${result.id}`);
      } else {
        setSuccess('Registration created successfully!');
      }
      
      // Refresh the list
      fetchRegistrations();
    } catch (error) {
      logger.error('Registration error:', error);
      setLoading(false);
      
      // Use centralized error mapping for user-friendly messages
      const errorMessage = mapErrorToUserMessage(error);
      setError(errorMessage);
    }
  };

  useEffect(() => {
    fetchRegistrations();
  }, [fetchRegistrations]);

  return (
    <Box className="gt-page gt-page-narrow">
      <AppPageHeader
        title="Sales tax registrations"
        description="Register in a state once you have nexus there, then track every registration in one list."
        actions={<Button variant="text" component={RouterLink} to="/nexus/dashboard">Check where you have nexus</Button>}
      />
      
      {/* Registration Form */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>Register for Sales Tax</Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Company Selection */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={loading}>
                <InputLabel id="company-select-label">Company</InputLabel>
                <Select
                  labelId="company-select-label"
                  id="company-select"
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  label="Company"
                  disabled={loading || loadingCompanies}
                >
                  <MenuItem value="">
                    <em>Select a company</em>
                  </MenuItem>
                  {companies && companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
                {loadingCompanies && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="caption">Loading companies...</Typography>
                  </Box>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                name="tax_id"
                label="Tax ID"
                value={selectedCompanyData?.tax_id || ''}
                placeholder="Enter tax ID"
                InputProps={{ readOnly: true }}
                helperText="Used for state registrations"
              />
            </Grid>

            {/* Connected Account Information - only show if company is selected and has a connected account */}
            {selectedCompany && selectedCompanyData && selectedCompanyData.tax_account_id && (
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Connected Account</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Chip 
                      label="Tax account connected" 
                      color="primary" 
                      variant="outlined" 
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Typography variant="body2">
                      This registration will use the company's connected account
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            )}

            {selectedCompanyData && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                  <Typography variant="subtitle2" gutterBottom>Registration will use this address:</Typography>
                  <Typography variant="body2">
                    {selectedCompanyData.address_line1 || 'No address line 1'}
                    {selectedCompanyData.address_line2 && `, ${selectedCompanyData.address_line2}`}
                    <br />
                    {selectedCompanyData.city || 'No city'}, {selectedCompanyData.state || 'No state'} {selectedCompanyData.zip_code || 'No zip code'}
                    <br />
                    {selectedCompanyData.country || 'No country'}
                  </Typography>
                </Paper>
              </Grid>
            )}
            
            {selectedConnectedAccount && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <strong>Company tax account:</strong> This registration will be filed under this company's GoTruTax tax account.
                  New registrations can take a few minutes to appear in the list below.
                </Alert>
              </Grid>
            )}
            
            {/* Country Selection */}
            <Grid item xs={12}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Country</InputLabel>
                <Select
                  value={country}
                  label="Country"
                  onChange={e => setCountry(e.target.value)}
                >
                  <MenuItem value="US">United States</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {country === 'US' && (
              <Grid item xs={12}>
                <TextField
                  select
                  label="State"
                  value={state}
                  onChange={e => setState(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                >
                  {US_STATES && US_STATES.map(s => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Registration Type</InputLabel>
                <Select
                  value={registrationType}
                  label="Registration Type"
                  onChange={e => setRegistrationType(e.target.value)}
                >
                  {US_REGISTRATION_TYPES && US_REGISTRATION_TYPES.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Radio
                      checked={useImmediateStart}
                      onChange={() => setUseImmediateStart(true)}
                    />
                  }
                  label="Start registration immediately (recommended)"
                />
                <FormControlLabel
                  control={
                    <Radio
                      checked={!useImmediateStart}
                      onChange={() => setUseImmediateStart(false)}
                    />
                  }
                  label="Start on specific date"
                />
              </FormControl>
            </Grid>
            
            {!useImmediateStart && (
              <Grid item xs={12}>
                <TextField
                  type="date"
                  label="Start date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                  inputProps={{
                    min: getTomorrowDate() // Prevent past dates
                  }}
                />
              </Grid>
            )}
            
            {/* Duplicate registrations are handled server-side */}
            
            {error && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              </Grid>
            )}
            
            {success && (
              <Grid item xs={12}>
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                fullWidth
              >
                Register for Sales Tax
                {loading && <CircularProgress size={24} sx={{ ml: 1 }} />}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      <Paper sx={{ p: 3, mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {selectedCompany && selectedCompanyData 
              ? `Registrations for ${selectedCompanyData.name}` 
              : 'All Active Tax Registrations'}
          </Typography>
          {selectedCompany && (
            <Chip 
              label={`Company ID: ${selectedCompany}`} 
              size="small" 
              variant="outlined" 
              color="primary"
            />
          )}
        </Box>
        
        <Alert severity="info" sx={{ mb: 2 }}>
          {selectedCompany 
            ? `Showing registrations for the selected company only. Clear company selection to view all registrations.`
            : `Showing all active tax registrations across all companies and states. Select a company above to filter registrations.`}
        </Alert>
        
        {listLoading ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : registrations.length === 0 ? (
          <Typography variant="body1" color="textSecondary" sx={{ my: 3, textAlign: 'center' }}>
            No tax registrations found.
          </Typography>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Country</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Start Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(Array.isArray(registrations) ? registrations : []).map((reg) => (
                  <TableRow key={reg.id} hover>
                    <TableCell>{reg.country}</TableCell>
                    <TableCell>{reg.state || '—'}</TableCell>
                    <TableCell>
                      {reg.company ? (
                        <Link 
                          component={RouterLink} 
                          to={`/companies/${reg.company.id}`}
                          underline="hover"
                          color="primary"
                        >
                          {reg.company.name}
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {reg.registration_type ? (
                        reg.registration_type.split('_').map(
                          word => word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={reg.status} 
                        size="small"
                        color={
                          reg.status === 'active' ? 'success' :
                          reg.status === 'pending' ? 'warning' :
                          reg.status === 'failed' ? 'error' : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {reg.raw && reg.raw.active_from ? 
                        new Date(reg.raw.active_from * 1000).toLocaleDateString() : 
                        (reg.start_date === 'now' ? 'Immediate' : reg.start_date)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default TaxRegistration; 